import { useState, useMemo, useEffect, useContext } from 'react'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import LiveReportRenderContext from '../../StateManagment/LiveReportRenderContext'
import {
    useFirestoreGeneral,
    useFirestoreGeneralOrderBy,
} from '../../firebase/useFirestore'
import OutputData from '../graphData/event/OutputData'
import EventData from '../graphData/event/EventData'
import OutputParticipantsData from '../graphData/event/OutputParticipantsData'
import AnalysisItemNavigator from '../analysis/AnalysisItemNavigator'
import DashboardCardOptions from '../dashboard/DashboardCardOptions'
import DashboardFilterBar from '../dashboard/DashboardFilterBar'
import DashboardPersonaFilter from '../dashboard/DashboardPersonaFilter'
import { getDefaultGraphType } from '../../helpers/visualisationDefaults'
import useChartColors from '../../hooks/useChartColors'
import filterRowsByPeriod from '../../helpers/liveReportHelpers/filterRowsByPeriod'

// Event-field types that expose a DashboardCardOptions footer (graph-type /
// colour controls). Scale & number get a primary colour; multiple-choice gets
// per-option colours. `Type === 'outputs'` is intentionally absent — those
// fields are filtered out of the navigable items below.

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Shared visualisation engine for Activity data. Used by both
// `AnalysisActivityCardBody` (dashboard) and `ActivityWidget` (live-report).
// Renders: optional navigator → optional filter bar → visualisation (Output or EventField).
//
// Header (title/description/dashboard-action) stays in the caller — this component
// owns only the visualisation engine. `renderItemAction(currentItem, activity)`
// is an optional slot for the per-item pin button rendered next to the navigator.
//
// displayMode: 'navigator' (default) shows one item at a time with prev/next
// chevrons; 'stacked' renders every item vertically.
//
// Two per-item settings sources are supported, in priority order:
//   1. nestedSettings[item.id] — used by the live-report widget
//      (settings.byNestedId, keyed by stable output/field ID).
//   2. dashboardItem maps — used by the dashboard: GraphTypeByOutput /
//      ColorOverridesByOutput for outputs, ColorOverridesByEventField for
//      event fields. Edits save back via DashboardCardOptions.
// Falls back to graphTypeOverride then the item's default per its type.
//
// templateActivityData: when set, items are sourced from the template blob
// instead of live Firestore — and the template thread is forwarded to the
// chart leaves (matches the existing OutputWidget/EffectWidget pattern).

/**
 * @param {object} props
 * @param {object} props.activity
 * @param {string} [props.graphTypeOverride]
 * @param {string[]} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {Array<object>} [props.personas]
 * @param {boolean} [props.personasEnabled]
 * @param {string} [props.selectedPersonaId]
 * @param {(personaId: string) => void} [props.onPersonaChange]
 * @param {(currentItem: object, activity: object) => React.ReactNode} [props.renderItemAction]
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {Object<string, Object<string, any>>} [props.nestedSettings]
 * @param {object|null} [props.dashboardItem] - DashboardItems doc (dashboard only). Source + save target for per-output / per-event-field overrides.
 * @param {object|null} [props.templateActivityData]
 * @param {(items: Array<{id: string, kind: 'output'|'eventField'}>) => void} [props.onItemsResolved]
 * @param {Date|null} [props.startDate] - Period lower bound for the live data layer
 * @param {Date|null} [props.endDate] - Period upper bound (end of day) for the live data layer
 * @param {{start: string|null, end: string|null}|null} [props.periodRange] - Resolved period as ISO dates; filters template-blob rows client-side
 * @param {boolean} [props.hideControls] - Suppress this component's own chrome (item navigator, persona filter, options footers) so a wrapping ComparisonFrame can own them once. See docs/features/compare-two-periods.md
 * @param {number} [props.visible] - Controlled item index (compare mode drives both periods with one shared navigator). Omit for internal navigation.
 * @param {React.Dispatch<React.SetStateAction<number>>} [props.onVisibleChange] - Setter for the controlled item index.
 * @param {(items: Array<object>) => void} [props.onItemsChange] - Reports the resolved item list so an external navigator knows the count.
 * @returns {JSX.Element}
 */
const ActivityVisualization = ({
    activity,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    renderItemAction,
    displayMode = 'navigator',
    nestedSettings,
    dashboardItem = null,
    onSaveOptions,
    templateActivityData,
    onItemsResolved,
    onCurrentItemChange,
    graphColor,
    startDate = null,
    endDate = null,
    periodRange = null,
    hideControls = false,
    visible: controlledVisible,
    onVisibleChange,
    onItemsChange,
}) => {
    // Navigation can be controlled (compare mode: one shared navigator drives
    // both periods) or internal (normal single-chart cards).
    const [internalVisible, setInternalVisible] = useState(0)
    const visible = controlledVisible != null ? controlledVisible : internalVisible
    const setVisible = onVisibleChange || setInternalVisible
    // Inside a live report the chart leaves hide their own title block
    // (OutputData gates on LiveReportRenderContext), so the navigator shows
    // the current item's title here instead. On the dashboard the leaves
    // render their own title — showing it here too would duplicate it.
    const inLiveReport = useContext(LiveReportRenderContext)
    const { palette, overrides } = useChartColors({ colorPalette, colorOverrides })

    const isTemplate = !!templateActivityData

    // Template mode: apply the resolved period client-side on the blob's
    // event-result rows — every template leaf (OutputData + the EventData
    // leaves via buildTemplateEventResult) reads outputs.eventResults, so one
    // filtered copy periodises them all. The shared blob is never mutated.
    const periodTemplateActivityData = useMemo(() => {
        if (!templateActivityData || !periodRange) return templateActivityData
        return {
            ...templateActivityData,
            outputs: {
                ...(templateActivityData.outputs || {}),
                eventResults: filterRowsByPeriod(templateActivityData.outputs?.eventResults || [], periodRange),
            },
        }
    }, [templateActivityData, periodRange])

    // Firestore — skipped in template mode (pass '' to short-circuit the listener).
    const liveOutputs = useFirestoreGeneralOrderBy('Outputs', 'ActivityID', isTemplate ? '' : (activity.ID || ''), 'Position', 'asc')
    const liveEventForms = useFirestoreGeneral('EventForms', 'ActivityID', isTemplate ? '' : (activity.ID || ''))
    const liveEventFormId = Array.isArray(liveEventForms) && liveEventForms.length > 0 ? liveEventForms[0].ID : ''
    const liveEventFields = useFirestoreGeneralOrderBy('EventFields', 'EventId', isTemplate ? '' : liveEventFormId, 'Position', 'asc')

    // Memoised: `items` below depends on these, and a fresh array every render
    // made `items` fresh too — in compare mode onItemsChange feeds that into
    // ParentCompareView's state, so it looped forever (inbox #335).
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const outputs = useMemo(() => (isTemplate
        ? (templateActivityData?.outputs?.outputs || []).filter(o => o.ActivityID === activity.ID)
        : (liveOutputs || []).filter(o => !o.Deleted)
    ), [isTemplate, templateActivityData, activity.ID, liveOutputs])

    const eventForms = isTemplate
        ? (templateActivityData?.outputs?.eventForms || []).filter(f => f.ActivityID === activity.ID)
        : liveEventForms

    const templateEventFormId = Array.isArray(eventForms) && eventForms.length > 0 ? eventForms[0].ID : ''
    const eventFields = useMemo(() => (isTemplate
        ? (templateActivityData?.outputs?.eventFields || [])
              .filter(f => f.EventId === templateEventFormId)
              .slice()
              .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
        : liveEventFields
    ), [isTemplate, templateActivityData, templateEventFormId, liveEventFields])

    // Combined navigable list: all outputs followed by non-date event fields.
    // `Type === 'outputs'` is a form-only meta-field that re-renders every
    // output stacked — redundant here since each output already has its own
    // navigator page.
    const items = useMemo(() => {
        const outputItems = Array.isArray(outputs)
            ? outputs.map(output => ({ kind: 'output', data: output, id: output.ID }))
            : []
        const eventFieldItems = Array.isArray(eventFields)
            ? eventFields
                .filter(field => field.Type !== 'date' && field.Type !== 'outputs')
                .map(field => ({ kind: 'eventField', data: field, id: field.ID }))
            : []

        // Deelnemers krijgen een eigen pagina in de navigator. Ze staan in
        // `Participants` op de outputregistraties, dus de grafiek leest het
        // outputs-veld — dat veld zelf is hierboven juist uitgefilterd omdat
        // het elke output nog eens gestapeld zou tonen.
        const outputsField = Array.isArray(eventFields)
            ? eventFields.find(field => field.Type === 'outputs')
            : null
        const participantItems = personasEnabled && outputsField
            ? [{ kind: 'participants', data: outputsField, id: `participants-${outputsField.ID}` }]
            : []

        return [...outputItems, ...eventFieldItems, ...participantItems]
    }, [outputs, eventFields, personasEnabled])

    // Surface the resolved items to the caller (used by the live-report widget
    // for one-shot migration of the legacy repeater[] shape).
    useEffect(() => {
        if (!onItemsResolved) return
        onItemsResolved(items)
    }, [items, onItemsResolved])

    // Surface the item list so a shared (compare) navigator knows the count.
    useEffect(() => {
        if (onItemsChange) onItemsChange(items)
    }, [items, onItemsChange])

    const currentItem = items[visible]

    // Surface the active carousel item upward so external UI (e.g. the
    // live-report editor sidebar) can highlight / auto-open the matching
    // controls. Only meaningful in navigator mode.
    useEffect(() => {
        if (!onCurrentItemChange) return
        onCurrentItemChange(displayMode === 'navigator' ? (currentItem || null) : null)
    }, [currentItem, displayMode, onCurrentItemChange])

    // ── Per-item dashboard settings (Idea #16) ──────────────────────────────
    const currentOutputId = currentItem?.kind === 'output' ? currentItem.data.ID : null

    // De optiebalk voor een event-veld bood alleen kleuren. Nu die keuze weg is
    // heeft een event-veld niets meer in te stellen — geen grafiektype, geen
    // weergave — dus rendert er ook geen balk meer.

    // Resolve graphType + colour for any item via: widget nestedSettings →
    // dashboardItem map → graphTypeOverride → type default. Output colours are
    // stored as a self-keyed sub-map ({ [outputId]: color }) to match the
    // DashboardCardOptions palette shape.
    const resolvePerItem = (item) => {
        // Kleur en grafiektype lopen niet gelijk op. Het grafiektype blijft een
        // keuze van de gebruiker, ook op een dashboardkaart; de kleur niet meer
        // — buiten een live rapportage komt die uit het vaste palet. Vandaar dat
        // alleen de kleurtakken op `overrides` leunen en de graphType-takken
        // ongemoeid blijven.
        if (!item) return { graphType: graphTypeOverride, color: null, eventColorOverrides: overrides }
        const widgetEntry = nestedSettings?.[item.id] || {}
        if (item.kind === 'output') {
            const dashColor = overrides && dashboardItem?.ColorOverridesByOutput?.[item.id]?.[item.id]
            const dashGraphType = dashboardItem?.GraphTypeByOutput?.[item.id]
            return {
                graphType: widgetEntry.graphType || dashGraphType || graphTypeOverride || getDefaultGraphType('output'),
                color: (overrides && widgetEntry.color) || dashColor || null,
                eventColorOverrides: overrides,
            }
        }
        const dashColors = overrides && dashboardItem?.ColorOverridesByEventField?.[item.id]
        return {
            graphType: widgetEntry.graphType || graphTypeOverride || getDefaultGraphType(item.data.Type),
            color: (overrides && widgetEntry.color) || dashColors?.primary || null,
            eventColorOverrides: dashColors || overrides,
        }
    }

    const currentResolved = resolvePerItem(currentItem)

    // Same label fallback chain as the editor sidebar (ActivityWidget's
    // resolveNestedItems), so the canvas and the controls name items alike.
    const itemLabel = (item) => item.kind === 'output'
        ? (item.data.Title || item.data.Output || 'Output')
        : (item.data.Title || item.data.Type)

    const saveOutputOptions = async (patch) => {
        // Analyse-page (transient) path — persist to caller state, not Firestore.
        if (onSaveOptions) {
            onSaveOptions(patch, currentOutputId, 'output')
            return
        }
        if (!dashboardItem?.docid || !currentOutputId) return
        const update = { Timestamp: firebase.firestore.FieldValue.serverTimestamp() }
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            update[`ColorOverridesByOutput.${currentOutputId}`] = patch.ColorOverrides
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'GraphType')) {
            update[`GraphTypeByOutput.${currentOutputId}`] = patch.GraphType
        }
        await db.collection('DashboardItems').doc(dashboardItem.docid).update(update)
    }

    const showPersonaFilter =
        displayMode === 'navigator'
            ? currentItem?.kind === 'output'
            : items.some(it => it.kind === 'output')
    const personaFilterVisible =
        showPersonaFilter &&
        Boolean(personasEnabled) &&
        Array.isArray(personas) &&
        personas.length > 0

    const renderOneItem = (item) => {
        const resolved = resolvePerItem(item)

        if (item.kind === 'output') {
            return (
                <OutputData
                    outputId={item.data.ID}
                    graphType={resolved.graphType}
                    graphColor={graphColor}
                    color={resolved.color || undefined}
                    personaId={selectedPersonaId || null}
                    startDate={startDate}
                    endDate={endDate}
                    templateOutputData={isTemplate ? periodTemplateActivityData?.outputs : null}
                />
            )
        }
        if (item.kind === 'participants') {
            return (
                <OutputParticipantsData
                    field={item.data}
                    color={resolved.color || undefined}
                    startDate={startDate}
                    endDate={endDate}
                    templateActivityData={periodTemplateActivityData}
                />
            )
        }

        return (
            <EventData
                field={item.data}
                selectedActivity={activity.ID}
                graphType={resolved.graphType}
                graphColor={graphColor}
                color={resolved.color || undefined}
                colorPalette={palette}
                colorOverrides={resolved.eventColorOverrides}
                startDate={startDate}
                endDate={endDate}
                templateActivityData={periodTemplateActivityData}
            />
        )
    }

    const renderNavigatorMode = () => {
        if (!currentItem) {
            return <p className="analysis-card-empty">Geen resultaten gevonden</p>
        }
        return renderOneItem(currentItem)
    }

    const renderStackedMode = () => {
        if (items.length === 0) {
            return <p className="analysis-card-empty">Geen resultaten gevonden</p>
        }
        return items.map(item => (
            <div key={item.id} className="analysis-card-visualisation-stacked-item">
                {renderOneItem(item)}
            </div>
        ))
    }

    return (
        <>
            {!hideControls && displayMode === 'navigator' && items.length > 1 && (
                <div className="analysis-card-navigator-row" data-copy-hide>
                    <AnalysisItemNavigator
                        items={items}
                        visible={visible}
                        setVisible={setVisible}
                    />
                    {renderItemAction && currentItem && (
                        <div className="analysis-card-item-pin">
                            {renderItemAction(currentItem, activity)}
                        </div>
                    )}
                </div>
            )}
            {inLiveReport && displayMode === 'navigator' && currentItem && (
                <p className="analysis-card-current-item-title">{itemLabel(currentItem)}</p>
            )}
            {!hideControls && personaFilterVisible && (
                <DashboardFilterBar
                    onClear={() => onPersonaChange('')}
                    activeFilterCount={selectedPersonaId ? 1 : 0}
                >
                    <DashboardPersonaFilter
                        personas={personas}
                        selectedPersonaId={selectedPersonaId || ''}
                        onChange={onPersonaChange}
                    />
                </DashboardFilterBar>
            )}
            <div className="analysis-card-visualisation">
                {displayMode === 'stacked' ? renderStackedMode() : renderNavigatorMode()}
            </div>
            {!hideControls && dashboardItem && currentItem?.kind === 'output' && (
                <DashboardCardOptions
                    item={dashboardItem}
                    fieldType="output"
                    currentGraphType={currentResolved.graphType}
                    subItems={[{ ID: currentItem.data.ID, label: currentItem.data.Output || currentItem.data.Title || 'Output' }]}
                    onSave={saveOutputOptions}
                    leadingBadge={items.length > 1 ? `${visible + 1} / ${items.length}` : null}
                />
            )}
        </>
    )
}

export default ActivityVisualization
